import React from 'react';
import styled from 'styled-components';
import CircleImage from './ui/CircleImage';
import Image from './ui/Image';
import PropTypes from 'prop-types';
import Anchor from './ui/Controls/Anchor';

const ImageWithCopyStyled = styled.div`
    display: flex;
    align-items: center;
    flex-direction: column;

    .circle-image-container {
        margin-bottom: 25px;
    }

    .square-image-container {
        margin-bottom: 25px;
        max-width: 100%;
    }

    .copy-container {
        padding-left: 36px;
        padding-right: 39px;
        margin-bottom: 50px;
    }

    .copy-header {
        color: ${(props) => props.theme.colors.texts.capeCod};
        font-family: ${(props) => props.theme.fonts[0]};
        font-size: 1.33rem;
        letter-spacing: 0.02px;
        line-height: 2rem;
        margin-bottom: 16px;
    }

    .copy-body {
        color: ${(props) => props.theme.colors.texts.shipGrey};
        font-family: ${(props) => props.theme.fonts[1]};
        font-size: 1rem;
        letter-spacing: 0;
        line-height: 2.11rem;
        margin-bottom: 10px;
    }

    .copy-link {
        font-family: ${(props) => props.theme.fonts[2]};
        font-size: 0.78rem;
        font-weight: bold;
        letter-spacing: 1.05px;
        line-height: 1.89rem;
        text-transform: uppercase;
        & > a {
            color: ${(props) => props.theme.colors.buttons.textLink};
        }
    }

    .square-image {
        margin-bottom: 10px;
    }

    @media screen and (min-width: 1024px) {
        display: flex;
        align-items: center;
        flex-direction: row;
        justify-content: center;
        width: 100%;
        padding: 0 90px;
        margin-bottom: 100px;

        .circle-image-container {
            display: flex;
            order: ${(props) => (props.imageLeft ? '1' : '2')};
            margin: ${(props) =>
                props.imageLeft ? '-166px 100px 0px 0px' : '0px 0px 0px 100px'};
        }

        .square-image-container {
            position: relative;
            display: flex;
            order: ${(props) => (props.imageLeft ? '1' : '2')};
            margin: ${(props) =>
                props.imageLeft ? '0px 100px 0px 0px' : '0px 100px 0px 50px'};
            max-width: 615px;
            width: 100%;
        }

        .copy-container {
            order: ${(props) => (props.imageLeft ? '2' : '1')};
            font-size: 1rem;
            max-width: 515px;
            padding-left: ${(props) => (props.imageLeft ? '4vw' : '0')};
            padding-right: ${(props) => (props.imageLeft ? '0' : '4vw')};
        }

        .copy-header {
            font-size: 1.875rem;
            letter-spacing: 0.03px;
            line-height: 2.5rem;
            margin-bottom: 1.25rem;
            max-width: 515px;
        }

        .copy-body {
            font-size: 1.25rem;
            letter-spacing: 0;
            line-height: 2.625rem;
            margin-bottom: 0.625 rem;
            max-width: 515px;
        }

        .copy-link {
            font-family: ${(props) => props.theme.fonts[0]};
            font-size: 0.875rem;
            font-weight: bold;
            letter-spacing: 1.05px;
            line-height: 2.125rem;
        }
    }

    @media screen and (min-width: 1024px) {
        .square-image-container {
            margin: ${(props) =>
                props.imageLeft ? '0px 20px 0px 0px' : '0px 0px 0px 20px'};
        }

        .circle-image {
            margin: ${(props) =>
                props.imageLeft ? '0px 50px 0px 0px' : '0px 0px 0px 50px'};
        }
    }

    @media only screen and (width: 768px) {
        padding: 1rem;
        margin-bottom: 2rem;

        .square-image-container {
            margin: 0;
        }

        .copy-container {
            margin: 0;
            flex-basis: 100%;
        }
    }
`;

export default function ImageWithCopy({
    size,
    imageLeft,
    imgSrc,
    squareImage,
    copyTitle,
    copyText,
    copyLinkText,
    linkTarget,
    showDotGrid,
}) {
    return (
        <ImageWithCopyStyled imageLeft={imageLeft}>
            {squareImage ? (
                <div className="square-image-container">
                    <Image
                        className="square-image"
                        hasLink={false}
                        imgAlt="Circle Component Demo"
                        imgSrc={imgSrc}
                        height="auto"
                        showDotGrid={showDotGrid}
                    />
                </div>
            ) : (
                <div className="circle-image-container">
                    <CircleImage
                        className="circle-image"
                        imgAlt="Circle Component Demo"
                        imgSrc={imgSrc}
                        imgTitle="Circle Component Demo"
                        windowSize={size}
                        isPrimary={false}
                    />
                </div>
            )}
            <div className="copy-container">
                <div className="copy-header">{copyTitle}</div>
                <div className="copy-body">{copyText}</div>
                {copyLinkText ? (
                    <Anchor
                        linkURL={linkTarget}
                        text={copyLinkText === null ? '' : `${copyLinkText} ›`}
                        isExternal={false}
                        className="copy-link"
                    />
                ) : null}
            </div>
        </ImageWithCopyStyled>
    );
}

ImageWithCopy.propTypes = {
    size: PropTypes.object.isRequired,
    imgSrc: PropTypes.string.isRequired,
    imageLeft: PropTypes.bool.isRequired,
    squareImage: PropTypes.bool.isRequired,
    copyTitle: PropTypes.string.isRequired,
    copyText: PropTypes.string.isRequired,
    copyLinkText: PropTypes.string,
    linkTarget: PropTypes.string,
    showDotGrid: PropTypes.bool,
};
